import styles from './NavMenuDrawer.module.scss';

import cx from 'classnames';
import React, { useEffect, useState, FC, HTMLAttributes } from 'react';
import { CSSTransition } from 'react-transition-group';

import type { LinkSet, NavigationItem } from 'api/contentful/contentTypes.cloverdotcom';
import { ContentLinkV2 } from 'components/ContentLinkV2/ContentLinkV2';
import { Icon, IconType } from 'components/Icon/Icon';
import { NavItemsList } from 'components/NavBar/NavItemsList/NavItemsList';
import { TextV2 } from 'components/TextV2/TextV2';
import type { FlatEntry } from 'services/cms/flattenContentful';
import { componentKeyV2 } from 'utils/ComponentKeyV2';
import { stringFormatForTestAttr } from 'utils/StringFormatForTest';
import { NAV_MENU_DRAWER_TEST_ID } from './NavMenuDrawer.const';

interface INavMenuDrawerProps extends HTMLAttributes<HTMLLIElement> {
  activeMenuIndex?: number;
  isDrawerClose?: boolean;
  menuItems?: Array<FlatEntry<NavigationItem>>;
  textUtilItems?: Array<FlatEntry<NavigationItem>>;
  openSubNavDrawer: (activeMenuIndex: number) => void;
  closeDrawer?: () => void;
}

export const NavMenuDrawer: FC<INavMenuDrawerProps> = ({
  menuItems = [],
  textUtilItems = [],
  closeDrawer,
  openSubNavDrawer,
  className,
  activeMenuIndex = -1,
  isDrawerClose,
  children,
  ...props
}) => {
  const [subMenu, setSubMenu] = useState<FlatEntry<NavigationItem> | undefined>(undefined);

  const activeMenu = menuItems[activeMenuIndex] as FlatEntry<LinkSet>;
  const hasSubMenu = (activeMenu?.items || []).length > 0;
  const tabIndex = hasSubMenu ? -1 : 0;

  useEffect(() => {
    if (activeMenu && hasSubMenu) {
      setSubMenu(activeMenu);
    }
  }, [activeMenu, hasSubMenu]);

  const hasNavMenuItems = [...menuItems, ...textUtilItems].length;

  if (!hasNavMenuItems) return null;

  return (
    <div className={cx(styles.navItemNarrow, className)}>
      <div
        className={cx(
          styles.navItemNarrowGroup,
          hasSubMenu && styles.navItemNarrowGroupToLeft,
          isDrawerClose && subMenu && styles.navItemNarrowGroupToMoreLeft,
        )}
      >
        <ul className={styles.navItemNarrowMainMenu} data-testid={NAV_MENU_DRAWER_TEST_ID.container}>
          {menuItems.map((entry, index) => {
            if (!entry) return null;
            const title = entry?.title;
            if (!title) return null;
            const isActive = index === activeMenuIndex;

            return (
              <li key={componentKeyV2(entry, index)} aria-expanded={isActive || undefined} {...props}>
                <button
                  className={styles.navItemNarrowButtonTitle}
                  onClick={() => openSubNavDrawer(index)}
                  disabled={hasSubMenu}
                  data-testid={`${stringFormatForTestAttr(title)}`}
                  tabIndex={tabIndex}
                >
                  <TextV2 value={title} variant="h4-medium" />
                  <Icon aria-hidden="true" type={IconType.CHEVRON_RIGHT} />
                </button>
              </li>
            );
          })}
          {textUtilItems.map((item, index) => (
            <li key={componentKeyV2(item, index)}>
              <ContentLinkV2
                entry={item}
                className={styles.navItemNarrowButtonTitle}
                disabled={hasSubMenu}
                tabIndex={tabIndex}
                variant="h4-medium"
              />
            </li>
          ))}
        </ul>
        <div className={styles.navItemNarrowSubMenu}>
          <CSSTransition in={hasSubMenu} timeout={parseInt(styles.duration)} unmountOnExit>
            <div>{subMenu && <NavItemsList entry={subMenu} closeDrawer={closeDrawer} />}</div>
          </CSSTransition>
        </div>
      </div>
    </div>
  );
};

NavMenuDrawer.displayName = 'NavMenuDrawer';
